import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { ItemTable, TRACKER_DEFAULT_COLUMNS } from "./Dashboard";
import { useAuth } from "../context/AuthContext";
import LiveCheckPanel from "../components/items/LiveCheckPanel";
import { Plus, Table2, LayoutGrid } from "lucide-react";
import PdfExportButton from "../components/PdfExportButton";
import NewItemModal from "./NewItemModal";
import MergePromptModal from "../components/dev-tracker/MergePromptModal";
import FilterBar from "../components/dev-tracker/FilterBar";
import ColumnsMenu from "../components/dev-tracker/ColumnsMenu";
import BoardView from "../components/dev-tracker/BoardView";
import LiveCount from "../components/LiveCount";
import { STATUSES, PRIORITIES, EFFORTS, PHASES } from "../constants/enums";

export default function Items() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [types, setTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [talmias, setTalmias] = useState([]);
  const [areaIds, setAreaIds] = useState([]);       // menu_area_id multi-select
  const [assignees, setAssignees] = useState([]);   // assigned_to multi-select
  const [areas, setAreas] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [ownership, setOwnership] = useState("all");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [view, setView] = useState("table");        // table | board
  const [verdict, setVerdict] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [columns, setColumns] = useState(TRACKER_DEFAULT_COLUMNS);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (types.length)      params.type = types.join(",");
      if (priorities.length) params.priority = priorities.join(",");
      if (statuses.length)   params.status = statuses.join(",");
      if (talmias.length)    params.talmia_version = talmias.join(",");
      if (assignees.length)  params.assigned_to = assignees.join(",");
      if (ownership === "mine")     params.assigned_to_me = "true";
      if (ownership === "created")  params.created_by_me = "true";
      if (verdict) params.verdict = verdict;
      if (showArchived) params.include_archived = "1";
      const { data } = await api.get("/items", { params });
      setItems(data);
    } finally { setLoading(false); }
  }, [search, types, priorities, statuses, talmias, assignees, ownership, verdict, showArchived]);

  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)); }, []);
  useEffect(() => { api.get("/dev/menu-areas").then(({ data }) => setAreas(data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const areasById = useMemo(() => Object.fromEntries(areas.map((a) => [a.id, a])), [areas]);
  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  // Client-side menu-area filter (multi-select).
  const visibleItems = useMemo(
    () => (areaIds.length ? items.filter((it) => areaIds.includes(it.menu_area_id)) : items),
    [items, areaIds],
  );

  const [counts, setCounts] = useState({ shown: 0, total: 0 });
  const onCounts = useCallback((shown, total) => setCounts({ shown, total }), []);

  // Inline edit / drag on the table + board — PATCH one item, patch local state.
  const patchItem = useCallback(async (id, patch) => {
    const { data } = await api.patch(`/items/${id}`, patch);
    setItems((arr) => arr.map((it) => (it.id === id ? data : it)));
    return data;
  }, []);

  const applyBatchPatch = async (patch) => {
    if (!selectedIds.length) return;
    try {
      await api.patch("/dev/items/batch", { item_ids: selectedIds, patch });
      setInfo(`Updated ${selectedIds.length} item${selectedIds.length === 1 ? "" : "s"}`);
      setTimeout(() => setInfo(""), 2500);
      setSelectedIds([]);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Batch update failed");
    }
  };
  const clearSelection = () => setSelectedIds([]);

  const [exporting, setExporting] = useState("");
  const [mergeOpen, setMergeOpen] = useState(false);
  const runExport = async (format) => {
    setExporting(format);
    try {
      const ids = selectedIds.length ? selectedIds : visibleItems.map((it) => it.id);
      const res = await api.post("/dev/items/export", { item_ids: ids, format }, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match ? match[1] : (format === "xlsx" ? "dev-tracker.xlsx" : "dev-tracker-claude.zip");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setInfo(`Exported ${ids.length} item${ids.length === 1 ? "" : "s"} as ${format.toUpperCase()}`);
      setTimeout(() => setInfo(""), 3000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Export failed");
    } finally {
      setExporting("");
    }
  };

  const clearAll = () => {
    setSearch(""); setTypes([]); setPriorities([]); setStatuses([]); setTalmias([]);
    setOwnership("all"); setAreaIds([]); setAssignees([]);
  };
  const hasFilter = !!(search || types.length || priorities.length || statuses.length || talmias.length || ownership !== "all" || areaIds.length || assignees.length);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError("");
    try {
      await api.delete(`/items/${pendingDelete.id}`);
      setItems((arr) => arr.filter((it) => it.id !== pendingDelete.id));
      setInfo(`Deleted "${pendingDelete.title}"`);
      setTimeout(() => setInfo(""), 3000);
      setPendingDelete(null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setDeleting(false); }
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight flex items-center gap-3">
            Dev &amp; Bug Tracker
            <LiveCount shown={counts.shown} total={visibleItems.length} testId="items-live-count" />
          </h1>
          <p className="text-slate-400 mt-1">
            Every bug, feature, improvement, chore and incident across Talmia 1 &amp; 2.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Table / Board view toggle */}
          <div className="inline-flex items-stretch gap-0.5 rounded-lg p-0.5 border border-white/10 bg-white/[0.02]" data-testid="view-toggle">
            <button type="button" onClick={() => setView("table")} data-testid="view-toggle-table"
              className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm ${view === "table" ? "bg-orange-500/15 text-orange-200" : "text-slate-400 hover:text-white"}`}>
              <Table2 className="w-4 h-4" /> Table
            </button>
            <button type="button" onClick={() => setView("board")} data-testid="view-toggle-board"
              className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm ${view === "board" ? "bg-orange-500/15 text-orange-200" : "text-slate-400 hover:text-white"}`}>
              <LayoutGrid className="w-4 h-4" /> Board
            </button>
          </div>
          {view === "table" && <ColumnsMenu visible={columns} onChange={setColumns} />}
          <PdfExportButton />
          <button
            type="button"
            onClick={() => runExport("xlsx")}
            disabled={!!exporting || (!selectedIds.length && !visibleItems.length)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm border border-emerald-400/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
            data-testid="items-export-xlsx"
          >
            {exporting === "xlsx" ? "Exporting…" : `XLSX${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
          </button>
          <button
            type="button"
            onClick={() => runExport("claude")}
            disabled={!!exporting || (!selectedIds.length && !visibleItems.length)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm border border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/20 disabled:opacity-40"
            data-testid="items-export-claude"
          >
            {exporting === "claude" ? "Bundling…" : `Claude bundle${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
          </button>
          <button
            type="button"
            onClick={() => setShowNew(true)}
            data-testid="items-new-cta"
            className="btn-coral inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Item
          </button>
        </div>
      </div>

      <LiveCheckPanel isAdmin={user?.role === "admin" || user?.access_role === "admin"} verdict={verdict} setVerdict={setVerdict} showArchived={showArchived} setShowArchived={setShowArchived}
        items={items} selectedIds={selectedIds} onChanged={load} onClearSelection={clearSelection} />
      <FilterBar
        search={search} setSearch={setSearch}
        talmias={talmias} setTalmias={setTalmias}
        areaIds={areaIds} setAreaIds={setAreaIds}
        types={types} setTypes={setTypes}
        statuses={statuses} setStatuses={setStatuses}
        priorities={priorities} setPriorities={setPriorities}
        assignees={assignees} setAssignees={setAssignees}
        ownership={ownership} setOwnership={setOwnership}
        areas={areas} users={users}
        onClearAll={clearAll} hasFilter={hasFilter}
      />

      <div className="card-dark p-5">
        {info && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 mb-4" data-testid="items-info">{info}</div>}
        {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4" data-testid="items-error">{error}</div>}

        {selectedIds.length > 0 && view === "table" && (
          <BatchActionBar
            count={selectedIds.length}
            areas={areaIds.length === 0 && talmias.length === 1 ? areas.filter((a) => a.system === talmias[0]) : areas}
            users={users}
            onClear={clearSelection}
            onSetArea={(area) => applyBatchPatch(area ? { menu_area: area.label, menu_area_id: area.id } : { menu_area: null, menu_area_id: null })}
            onSetStatus={(s) => applyBatchPatch({ status: s })}
            onSetPriority={(p) => applyBatchPatch({ priority: p })}
            onSetAssignee={(a) => applyBatchPatch({ assigned_to: a })}
            onSetEffort={(e) => applyBatchPatch({ effort: e || null })}
            onSetPhase={(p) => applyBatchPatch({ phase: p || null })}
            onMerge={() => setMergeOpen(true)}
          />
        )}

        {view === "table" ? (
          <ItemTable
            items={visibleItems}
            usersById={usersById}
            loading={loading}
            emptyMessage="No items match your filters."
            isAdmin={user?.role === "admin"}
            onDelete={(it) => setPendingDelete(it)}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            areasById={areasById}
            infinite
            onCounts={onCounts}
            editable
            onPatchItem={patchItem}
            visibleColumns={columns}
            users={users}
            areas={areas}
          />
        ) : (
          loading
            ? <div className="p-6 text-slate-400 text-sm">Loading...</div>
            : <BoardView items={visibleItems} usersById={usersById} onPatchItem={(id, patch) => patchItem(id, patch).then(() => toast.success("Moved")).catch(() => toast.error("Move failed"))} />
        )}
      </div>

      {pendingDelete && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" data-testid="delete-item-modal" onClick={() => !deleting && setPendingDelete(null)}>
          <div className="card-dark w-full max-w-md p-6 fade-up" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold mb-3">Delete this item?</h3>
            <p className="text-sm text-slate-300">
              Permanently remove <b>{pendingDelete.title}</b> along with all of its comments and activity history. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 pt-5">
              <button type="button" onClick={() => setPendingDelete(null)} disabled={deleting} className="px-3 py-2 rounded-md text-sm border border-white/10 hover:bg-white/5 disabled:opacity-50">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} className="px-4 py-2 rounded-md text-sm font-semibold bg-red-600 text-white hover:bg-red-500 disabled:opacity-50" data-testid="delete-item-confirm">{deleting ? "Deleting..." : "Yes, delete"}</button>
            </div>
          </div>
        </div>
      )}

      <NewItemModal open={showNew} onClose={() => setShowNew(false)} onCreated={() => load()} />
      <MergePromptModal open={mergeOpen} itemIds={selectedIds} onApplied={() => load()} onClose={() => setMergeOpen(false)} />
    </Layout>
  );
}


// ─── Sticky batch-action bar ─────────────────────────────────────────
function BatchActionBar({ count, areas, users, onClear, onSetArea, onSetStatus, onSetPriority, onSetAssignee, onSetEffort, onSetPhase, onMerge }) {
  const sel = (onPick, opts, testid, placeholder = "Set…") => (
    <select
      onChange={(e) => { if (e.target.value !== "__") { onPick(e.target.value === "__clear__" ? null : e.target.value); e.target.value = "__"; } }}
      className="input-dark py-1 pl-2 pr-6 text-xs w-auto"
      data-testid={testid}
      defaultValue="__"
    >
      <option value="__" disabled>{placeholder}</option>
      {opts}
    </select>
  );
  return (
    <div className="sticky top-0 z-30 mb-3 p-2.5 rounded-lg border border-orange-400/40 bg-slate-900/90 backdrop-blur-md shadow-lg flex flex-wrap items-center gap-2" data-testid="batch-action-bar">
      <span className="text-xs font-mono uppercase tracking-widest text-orange-200">{count} selected</span>
      <span className="text-white/20">·</span>

      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-400 font-mono tracking-widest pr-1">Area</span>
        {sel(
          (v) => { if (v === null) return onSetArea(null); const a = areas.find((x) => x.id === v); if (a) onSetArea(a); },
          <>
            <option value="__clear__">— clear area —</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </>,
          "batch-set-area",
        )}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-400 font-mono tracking-widest pr-1">Status</span>
        {sel(onSetStatus, STATUSES.map((s) => <option key={s}>{s}</option>), "batch-set-status")}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-400 font-mono tracking-widest pr-1">Priority</span>
        {sel(onSetPriority, PRIORITIES.map((p) => <option key={p}>{p}</option>), "batch-set-priority")}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-400 font-mono tracking-widest pr-1">Assignee</span>
        {sel(onSetAssignee, <>
          <option value="__clear__">Unassigned</option>
          <optgroup label="Team"><option value="emergent">Emergent</option><option value="joint">Joint</option><option value="gami">GAMI</option></optgroup>
          <optgroup label="People">{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</optgroup>
        </>, "batch-set-assignee")}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-400 font-mono tracking-widest pr-1">Effort</span>
        {sel(onSetEffort, [<option key="c" value="__clear__">—</option>, ...EFFORTS.map((e) => <option key={e}>{e}</option>)], "batch-set-effort")}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase text-slate-400 font-mono tracking-widest pr-1">Phase</span>
        {sel(onSetPhase, [<option key="c" value="__clear__">—</option>, ...PHASES.map((p) => <option key={p}>{p}</option>)], "batch-set-phase")}
      </div>

      <button type="button" onClick={onMerge}
        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-fuchsia-400/40 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100"
        data-testid="batch-merge">
        ✨ Merge → build brief
      </button>
      <button type="button" onClick={onClear} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-white/5" data-testid="batch-clear">
        Clear selection
      </button>
    </div>
  );
}
